<template>
  <div v-click-outside="onClickOutside" :class="classes">
    <div class="ewr-select__output" @click="onOutputClick">
      <slot
        name="output"
        :item="selectedItems.length > 0 ? selectedItems[0] : null"
      >
        <span>{{ output }}</span>
      </slot>
    </div>

    <!-- SINGLE OPTION -->
    <div v-if="open && !multiple" class="ewr-select__options">
      <div
        v-for="(item, index) in data"
        :key="index"
        class="ewr-select__item"
        @click="onItemClick(item)"
      >
        <slot :item="item">{{ labelProp ? item[labelProp] : item }}</slot>
      </div>
    </div>

    <!-- MULTIPLE OPTIONS -->
    <div
      v-if="open && multiple"
      class="ewr-select__options ewr-select__options--multiple"
    >
      <div
        v-for="(item, index) in data"
        :key="index"
        class="ewr-select__item"
        @click="onItemClick(item)"
      >
        <slot :item="item">
          <input v-model="selectedItems" type="checkbox" :value="item" />
          <span>{{ labelProp ? item[labelProp] : item }}</span>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import _find from 'lodash/find'
import _filter from 'lodash/filter'
import _map from 'lodash/map'
import _includes from 'lodash/includes'
import _findIndex from 'lodash/findIndex'
import _reduce from 'lodash/reduce'

import ClickOutside from 'vue-click-outside'

export default {
  directives: { ClickOutside },

  props: {
    multiple: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    small: { type: Boolean, default: false },
    labelProp: {
      type: String,
      default: null
    },
    valueProp: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: 'Select'
    },
    name: {
      type: String,
      default: null
    },
    value: {
      type: null,
      default: null
    },
    data: {
      type: Array,
      default: () => []
    }
  },

  data: () => ({
    open: false,
    selectedItems: []
  }),

  computed: {
    classes() {
      return [
        'ewr-select',
        {
          'ewr-select--null': this.selectedItems.length === 0,
          'ewr-select--disabled': this.disabled,
          'ewr-select--small': this.small
        }
      ]
    },

    output() {
      // console.log('[EwrSelect] selectedItems output', this.selectedItems)
      if (this.selectedItems.length > 0) {
        return this.multiple
          ? _reduce(
              this.selectedItems,
              (acc, item) => {
                let string = this.labelProp
                  ? acc.string + `${item[this.labelProp]}`
                  : acc.string + `${item}`

                string =
                  this.selectedItems.length > 1 &&
                  acc.index < this.selectedItems.length - 1
                    ? string + ', '
                    : string

                return { index: acc.index + 1, string }
              },
              { index: 0, string: '' }
            ).string
          : this.labelProp
          ? this.selectedItems[0][this.labelProp]
          : this.selectedItems[0]
      } else {
        return this.placeholder
      }
    }
  },

  watch: {
    data() {
      this.find(this.value)
    },
    value(value) {
      this.find(value)
    },
    selectedItems(items) {
      let value = this.multiple ? [] : null
      if (items.length > 0) {
        value = this.multiple
          ? this.valueProp
            ? _map(items, (item) => item[this.valueProp])
            : items
          : this.valueProp
          ? items[0][this.valueProp]
          : items[0]
      }

      // console.log('[EwrSelect] watch selectedItems value', value, this.value)
      if (JSON.stringify(value) !== JSON.stringify(this.value)) {
        this.$emit('input', value)
      }
    }
  },

  created() {
    this.find(this.value)
  },

  methods: {
    find(value) {
      if (value !== null) {
        if (this.valueProp) {
          let items
          if (this.multiple) {
            items = _filter(this.data, (item) =>
              _includes(value, item[this.valueProp])
            )
          } else {
            const item = _find(
              this.data,
              (item) => item[this.valueProp] === value
            )
            items = item ? [item] : []
          }
          if (items.length > 0) {
            this.selectedItems = items
          } else {
            console.warn(
              `[EwrSelect] Item matching '${this.valueProp}: ${this.value}' not found in data.`
            )
            this.selectedItems = []
          }
        } else {
          this.selectedItems = this.multiple ? value : [value]
        }
      } else {
        this.selectedItems = []
      }
    },

    /**
     * Determine if we need to add or remove the item from the selectedItems
     * list. If `!multiple`, we simply set `selectedItems` to
     * the item wrapped in an array.
     */
    onItemClick(item) {
      // console.log('[select] onItemClick', item, this.selectedItems)
      if (this.multiple) {
        const itemIndex = _findIndex(
          this.selectedItems,
          (selectedItem) => selectedItem === item
        )
        if (itemIndex >= 0) {
          // console.log('[select] removing item', item)
          this.selectedItems.splice(itemIndex, 1)
        } else {
          // console.log('[select] adding item', item)
          this.selectedItems.push(item)
        }
      } else {
        // console.log('[select] setting item', item)
        this.selectedItems = [item]
        this.open = false
      }
    },

    /**
     * onOutputClick handler
     * We delay `open = true` to prevent conflict with `onClickOutside`.
     * NOTE: the conflict doesn't seem to be a problem until we
     * build the web component.
     */
    onOutputClick() {
      // console.log('[select] onOutputClick')

      if (!this.open) {
        setTimeout(() => {
          this.open = !this.open
        }, 100)
      } else {
        this.open = false
      }
    },

    /**
     * For webcomponents, we need a little extra logic to make click-outide work
     * due to "retargeting" that happens in webcomponents (not Vue specific).
     * See: https://lamplightdev.com/blog/2021/04/10/how-to-detect-clicks-outside-of-a-web-component/
     */
    onClickOutside(event) {
      // console.log('[select] onClickOutside')

      const composedPathIncludesThis = event.composedPath().includes(this.$el)
      // console.log('contains options', composedPathIncludesThis)

      if (this.open && !composedPathIncludesThis) {
        this.open = false
      }
    }
  }
}
</script>

<style lang="scss">
.ewr-select {
  @include border-box;
  position: relative;
  width: 300px;
  max-width: 100%;

  @include bpXs {
    width: 100%;
  }

  &:after {
    // @include FontAwesome($fa-var-caret-down);
    color: $black;
    position: absolute;
    right: 15px;
    top: 0px;
    font-size: 20px;
    line-height: blh(2);
    pointer-events: none;
  }

  &__output {
    @include OpenSans;
    @include border-box;
    font-size: type-scale(0);
    padding: 0px blh(0.5);
    line-height: blh(2);
    font-weight: $font_weight_normal;
    width: 100%;
    height: blh(2);
    background-color: white;
    border: 1px solid $gray300;
    color: $gray800;
    border-radius: 0px;
    cursor: pointer;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: blh(1.5);
  }

  &__options {
    @include list-reset;
    position: absolute;
    top: blh(2);
    left: 0px;
    border: 1px solid $gray300;
    border-top: none;
    box-shadow: 1px 1px 4px rgba($gray700, 0.15);
    background: white;
    width: 100%;
    z-index: 10;
    max-height: blh(9);
    overflow-y: scroll;

    .ewr-select__item {
      @include line-height(2);
      position: relative;
      color: $gray800;
      padding: 0px blh(0.5);
      cursor: pointer;
      border-top: 1px solid darken($gray300, 3%);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &:first-child {
        border: none;
      }

      &:hover {
        background: $teal;
        color: white;
      }
    }

    &--multiple {
      .ewr-select__item {
        padding-left: blh(0.5);

        input[type='checkbox'] {
          margin-left: 0px;
        }
      }
    }
  }

  &--disabled {
    pointer-events: none;

    .ewr-select__output {
      background-color: $gray50;
      color: $gray500;
    }

    &:after {
      color: $gray500;
    }
  }

  &--null {
    select {
      color: darken(white, 30%);
    }
  }

  &--small {
    height: blh(1.5);
    line-height: blh(1.5);

    &:after {
      line-height: blh(1.5);
    }

    .ewr-select__output {
      height: blh(1.5);
      line-height: blh(1.5);
    }

    .ewr-select__options {
      top: blh(1.5);
    }
  }
}

.form__field--infield-top-aligned {
  .ewr-select {
    height: blh(2.5);

    &__output {
      padding-top: $blh;
      height: 100%;
      line-height: $blh;
    }

    .ewr-select__options {
      top: blh(2.5);
    }

    &:after {
      line-height: blh(2.5);
    }
  }
}
</style>
